import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import xstream from "state-x-stream";
import {
  BehaviorSubject,
  map,
  tap,
  takeUntil,
  switchMap,
  catchError,
  of,
  distinctUntilChanged,
  filter,
  auditTime,
  throttleTime,
  merge,
} from "rxjs";
import ImperativeTooltip from "../../../../components/atoms/Tooltip/ImperativeTooltip";
import Track from "../../models/Track";
import Variant from "../../../../models/Variant";
import Region from "../../../../models/Region";
import isWithinClass from "../../../../common/helpers/isWithinClass";

function controller({ props$, destroyed$ }) {
  const mousePxFixY = 2; // adjust pointer y pos
  const mousePxFixX = -2; // adjust pointer x pos

  const mousePosTracker$ = new BehaviorSubject({});
  const anchorEl$ = new BehaviorSubject(null);
  const containerEl$ = new BehaviorSubject(null);
  const tooltipItem$ = new BehaviorSubject(null);
  const titleItem$ = new BehaviorSubject(null);
  const isActive$ = new BehaviorSubject(false);

  function getRaycastItem() {
    const { offsetX, offsetY } = mousePosTracker$.value;
    if (!offsetX || !offsetY) return null;
    const { zoomService: zs } = props$.value.track;
    const { track } = props$.value;
    const { stage, absoluteOffsetY } = track;
    const { y: scroll } = zs.zoomId;
    const x = Math.floor(offsetX) + mousePxFixX;
    const y = Math.floor(offsetY + absoluteOffsetY + scroll) + mousePxFixY;
    const { meshes } = track;
    return meshes.length > 0 ? stage.mainScene.raycast(meshes, x, y) : null;
  }

  function updateAnhorPos() {
    const { offsetX, offsetY } = mousePosTracker$.value;
    if (!offsetX || !offsetY) return;
    const transform = `translate(${offsetX}px, ${offsetY}px)`;
    if (anchorEl$?.value?.style) {
      anchorEl$.value.style.transform = transform;
    }
  }

  function updateCursor(foundItem) {
    if (containerEl$.value?.style) {
      containerEl$.value.style.cursor = foundItem ? "pointer" : "default";
    }
  }

  function errorHandler(e) {
    console.error(e);
    return of(null);
  }

  function deactivate() {
    isActive$.next(false);
    titleItem$.next(null);
  }

  function activate() {
    isActive$.next(true);
  }

  function handleClick({ offsetX, offsetY }) {
    updateAnhorPos({ offsetX, offsetY });
    deactivate();
    const clicked = getRaycastItem({ offsetX, offsetY });
    if (clicked) tooltipItem$.next({ ...clicked, key: new Date().getTime() });
  }

  function recalcRelativePos() {
    const zs = props$.value.track.zoomService;
    const left = zs.mousePosX$.value;
    const top = zs.mousePosY$.value;

    const offsetX = left;
    const offsetY = top - props$.value.track.absoluteOffsetY;

    if (offsetY < 0 || offsetY > props$.value.track.totalHeight) {
      mousePosTracker$.next({ offsetX: 0, offsetY: 0 });
      return;
    }

    mousePosTracker$.next({ offsetX, offsetY });
  }

  // track mouse position.
  props$
    .pipe(
      switchMap(({ track }) => track.zoomService.mousePos$),
      auditTime(80),
      catchError(errorHandler),
      takeUntil(destroyed$)
    )
    .subscribe(recalcRelativePos);

  props$
    .pipe(
      switchMap(({ track }) => track.zoomService.xVisScale$),
      auditTime(100),
      tap(deactivate),
      catchError(errorHandler)
    )
    .subscribe();

  // workflow after mouse position changed
  mousePosTracker$
    .pipe(
      map(getRaycastItem),
      tap((x) => !x && titleItem$.next(null)),
      tap((x) => updateCursor(x)),
      tap((x) => x && updateAnhorPos()),
      tap((x) => titleItem$.next(isActive$.value ? x : null)),
      takeUntil(destroyed$),
      catchError(errorHandler)
    )
    .subscribe();

  function isEqual(a, b) {
    return a?.mesh === b?.mesh && a?.index === b?.index;
  }

  return {
    deactivate,
    activate,
    tooltipItem: tooltipItem$,
    titleItem: merge(
      titleItem$.pipe(filter((x) => x === null)),
      titleItem$.pipe(
        distinctUntilChanged(isEqual),
        throttleTime(300, undefined, { leading: false, trailing: true })
      )
    ),
    checkPreventClose: (e) => isWithinClass(e.target, "prevent-tooltip-close"),
    setTooltipItem: (x) => tooltipItem$.next(x),
    setAnchorEl: (el) => anchorEl$.next(el),
    setContainerEl: (el) => containerEl$.next(el),
    handleClick,
  };
}

function MeshTooltipContainer({
  deactivate,
  activate,
  handleClick,
  setAnchorEl,
  setContainerEl,
  track,
  checkPreventClose,
  tooltipItem,
  titleItem,
  setTooltipItem,
}) {
  const borderRadius = 9;
  const containerRef = useRef(null);
  const anchorRef = useRef(null);
  const TitleComp = track.TitleRenderer;
  const TooltipComp = track.TooltipRenderer;
  const isTooltip = tooltipItem && Boolean(TooltipComp);
  const allowTitle = !tooltipItem;

  useEffect(() => {
    setAnchorEl(anchorRef.current);
    setContainerEl(containerRef.current);
  }, [setAnchorEl]);

  return (
    <>
      <div
        tabIndex={-1}
        ref={containerRef}
        className="tw-absolute tw-inset-0"
        onPointerDown={deactivate}
        onMouseLeave={deactivate}
        onMouseMove={activate}
        onClick={(e) => {
          const { offsetX, offsetY } = e.nativeEvent;
          handleClick({ offsetX, offsetY });
        }}
      />
      <div
        className="tw-absolute tw-top-0 tw-left-0 tw-w-px tw-h-px tw-pointer-events-none"
        ref={anchorRef}
      />

      {isTooltip ? (
        <ImperativeTooltip
          key={tooltipItem?.key}
          borderRadius={borderRadius}
          onClose={() => setTooltipItem(null)}
          anchor={anchorRef.current}
          className="genome-browser-tooltip"
          checkPreventClose={checkPreventClose}
          zIndex={9999}
          ContentRenderer={() => (
            <TooltipComp
              currentInd={tooltipItem.index}
              track={track}
              item={tooltipItem.item}
              requestClose={() => setTooltipItem(null)}
            />
          )}
        />
      ) : null}

      {TitleComp && titleItem && allowTitle ? (
        <ImperativeTooltip
          onClose={() => {}}
          borderRadius={borderRadius}
          anchor={anchorRef.current}
          zIndex={9999}
          followMouse={true}
          className="genome-browser-title"
          ContentRenderer={() => (
            <TitleComp
              key={titleItem?.index}
              currentInd={titleItem.index}
              track={track}
              item={titleItem.item}
            />
          )}
        />
      ) : null}
    </>
  );
}

MeshTooltipContainer.propTypes = {
  deactivate: PropTypes.func.isRequired,
  activate: PropTypes.func.isRequired,
  setTooltipItem: PropTypes.func.isRequired,
  setAnchorEl: PropTypes.func.isRequired,
  setContainerEl: PropTypes.func.isRequired,
  checkPreventClose: PropTypes.func.isRequired,
  handleClick: PropTypes.func.isRequired,
  track: PropTypes.instanceOf(Track).isRequired,
  tooltipItem: PropTypes.shape({
    key: PropTypes.number,
    index: PropTypes.number,
    item: PropTypes.shape({
      variant: PropTypes.instanceOf(Variant),
      region: PropTypes.instanceOf(Region),
    }),
  }),
  titleItem: PropTypes.shape({
    index: PropTypes.number,
    item: PropTypes.shape({}),
  }),
};

MeshTooltipContainer.defaultProps = {
  titleItem: null,
  tooltipItem: null,
};

export default xstream(controller)(MeshTooltipContainer);
